import React from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ModelConfig } from '../services/stt';

export const availableModels: ModelConfig[] = [
  // Transcription Models
  {
    id: 'gpt-4o-transcribe',
    name: 'OpenAI GPT-4o Transcribe',
    type: 'transcribe',
    isEnabled: true,
    provider: 'openai',
    model: 'gpt-4o-transcribe'
  },
  {
    id: 'gpt-4o-mini-transcribe',
    name: 'OpenAI GPT-4o Mini Transcribe',
    type: 'transcribe',
    isEnabled: true,
    provider: 'openai',
    model: 'gpt-4o-mini-transcribe'
  },
  {
    id: 'whisper-1',
    name: 'OpenAI Whisper',
    type: 'transcribe',
    isEnabled: true,
    provider: 'openai',
    model: 'whisper-1'
  },
  {
    id: 'elevenlabs-transcribe',
    name: 'ElevenLabs',
    type: 'transcribe',
    isEnabled: true,
    provider: 'elevenlabs',
    model: 'whisper-1'
  },
  {
    id: 'deepgram-nova2',
    name: 'Deepgram Nova-2',
    type: 'transcribe',
    isEnabled: true,
    provider: 'deepgram',
    model: 'nova-2'
  },
  {
    id: 'deepgram-nova3',
    name: 'Deepgram Nova-3',
    type: 'transcribe',
    isEnabled: true,
    provider: 'deepgram',
    model: 'nova-3'
  },
  {
    id: 'google-gemini-flash-transcribe',
    name: 'Google Gemini 2.0 Flash Transcribe',
    type: 'transcribe',
    isEnabled: true,
    provider: 'google',
    model: 'gemini-2.0-flash'
  },
  {
    id: 'google-gemini-flash-lite-transcribe',
    name: 'Google Gemini 2.0 Flash Lite Transcribe',
    type: 'transcribe',
    isEnabled: true,
    provider: 'google',
    model: 'gemini-2.0-flash-lite'
  },
  {
    id: 'google-gemini-2-5-pro-preview-transcribe',
    name: 'Google Gemini 2.5 Pro Experimental Transcribe',
    type: 'transcribe',
    isEnabled: true,
    provider: 'google',
    model: 'gemini-2.5-pro-exp-03-25'
  },
  // Translation Models (always to English)
  {
    id: 'claude-3-7-sonnet-translate',
    name: 'Anthropic Claude 3.7 Sonnet Translate',
    type: 'translate',
    isEnabled: true,
    provider: 'anthropic',
    model: 'claude-3-7-sonnet-latest',
    targetLanguage: 'en'
  },
  {
    id: 'whisper-translate',
    name: 'OpenAI Whisper Auto-Translate to English',
    type: 'translate',
    isEnabled: true,
    provider: 'openai',
    model: 'whisper-1',
    targetLanguage: 'en'
  },
  {
    id: 'google-gemini-flash-translate',
    name: 'Google Gemini 2.0 Flash Translate to English',
    type: 'translate',
    isEnabled: true,
    provider: 'google',
    model: 'gemini-2.0-flash',
    targetLanguage: 'en'
  },
  {
    id: 'google-gemini-flash-lite-translate',
    name: 'Google Gemini 2.0 Flash Lite Translate to English',
    type: 'translate',
    isEnabled: true,
    provider: 'google',
    model: 'gemini-2.0-flash-lite',
    targetLanguage: 'en'
  },
  {
    id: 'google-gemini-2-5-pro-preview-translate',
    name: 'Google Gemini 2.5 Pro Experimental Translate to English',
    type: 'translate',
    isEnabled: true,
    provider: 'google',
    model: 'gemini-2.5-pro-exp-03-25',
    targetLanguage: 'en'
  }
];

interface ModelSelectorProps {
  selectedModels: ModelConfig[];
  onModelsChange: (models: ModelConfig[]) => void;
  disabled?: boolean;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ selectedModels, onModelsChange, disabled }) => {
  const [open, setOpen] = React.useState(false);
  const [selectAllState, setSelectAllState] = React.useState<Record<string, boolean>>({
    all: false,
    transcribe: false,
    translate: false,
    openai: false,
    google: false,
    other: false
  });

  // Initialize with all available models if selectedModels is empty
  React.useEffect(() => {
    if (selectedModels.length === 0) {
      onModelsChange(availableModels.map(model => ({ ...model, isEnabled: true })));
    }
  }, []);

  // Update selectAllState when selectedModels changes
  React.useEffect(() => {
    const allModels = availableModels.length === selectedModels.filter(m => m.isEnabled).length;
    const allTranscribe = availableModels.filter(m => m.type === 'transcribe').length === 
      selectedModels.filter(m => m.type === 'transcribe' && m.isEnabled).length;
    const allTranslate = availableModels.filter(m => m.type === 'translate').length === 
      selectedModels.filter(m => m.type === 'translate' && m.isEnabled).length;
    const allOpenAI = availableModels.filter(m => m.provider === 'openai').length === 
      selectedModels.filter(m => m.provider === 'openai' && m.isEnabled).length;
    const allGoogle = availableModels.filter(m => m.provider === 'google').length === 
      selectedModels.filter(m => m.provider === 'google' && m.isEnabled).length;
    const allOther = availableModels.filter(m => m.provider !== 'openai' && m.provider !== 'google').length === 
      selectedModels.filter(m => m.provider !== 'openai' && m.provider !== 'google' && m.isEnabled).length;

    setSelectAllState({
      all: allModels,
      transcribe: allTranscribe,
      translate: allTranslate,
      openai: allOpenAI,
      google: allGoogle,
      other: allOther
    });
  }, [selectedModels]);

  const handleToggle = (model: ModelConfig) => {
    const updatedModels = selectedModels.map(m => 
      m.id === model.id ? { ...m, isEnabled: !m.isEnabled } : m
    );
    if (!selectedModels.find(m => m.id === model.id)) {
      updatedModels.push({ ...model, isEnabled: true });
    }
    onModelsChange(updatedModels);
  };

  const handleSelectAll = (type: 'transcribe' | 'translate' | 'all' | 'openai' | 'google' | 'other', enabled: boolean) => {
    let modelsToUpdate = availableModels;
    
    if (type === 'transcribe' || type === 'translate') {
      modelsToUpdate = availableModels.filter(m => m.type === type);
    } else if (type === 'openai') {
      modelsToUpdate = availableModels.filter(m => m.provider === 'openai');
    } else if (type === 'google') {
      modelsToUpdate = availableModels.filter(m => m.provider === 'google');
    } else if (type === 'other') {
      modelsToUpdate = availableModels.filter(m => m.provider !== 'openai' && m.provider !== 'google');
    }

    const updatedModels = [...selectedModels];
    
    modelsToUpdate.forEach(model => {
      const existingIndex = updatedModels.findIndex(m => m.id === model.id);
      if (existingIndex >= 0) {
        updatedModels[existingIndex] = { ...model, isEnabled: enabled };
      } else {
        updatedModels.push({ ...model, isEnabled: enabled });
      }
    });

    onModelsChange(updatedModels);
  };

  const handleAddModel = () => {
    setOpen(true);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Active Models</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleAddModel}
          color="primary"
          disabled={disabled}
        >
          Add Model
        </Button>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Models</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAllState.all}
                  onChange={(e) => handleSelectAll('all', e.target.checked)}
                  indeterminate={selectedModels.some(m => m.isEnabled) && !selectAllState.all}
                  disabled={disabled}
                />
              }
              label="Select All Models"
            />
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllState.transcribe}
                    onChange={(e) => handleSelectAll('transcribe', e.target.checked)}
                    indeterminate={
                      selectedModels.some(m => m.type === 'transcribe' && m.isEnabled) && 
                      !selectAllState.transcribe
                    }
                    disabled={disabled}
                  />
                }
                label="All Transcription"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllState.translate}
                    onChange={(e) => handleSelectAll('translate', e.target.checked)}
                    indeterminate={
                      selectedModels.some(m => m.type === 'translate' && m.isEnabled) && 
                      !selectAllState.translate
                    }
                    disabled={disabled}
                  />
                }
                label="All Translation"
              />
            </Box>
          </Box>

          <List>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                OpenAI Models
              </Typography>
              <FormControlLabel
                sx={{ ml: 2 }}
                control={
                  <Checkbox
                    checked={selectAllState.openai}
                    onChange={(e) => handleSelectAll('openai', e.target.checked)}
                    indeterminate={
                      selectedModels.some(m => m.provider === 'openai' && m.isEnabled) && 
                      !selectAllState.openai
                    }
                    size="small"
                  />
                }
                label="Select All"
              />
            </Box>
            {availableModels
              .filter(model => model.provider === 'openai')
              .map((model) => {
                const isSelected = selectedModels.some(m => m.id === model.id && m.isEnabled);
                return (
                  <ListItem key={model.id}>
                    <ListItemText 
                      primary={model.name}
                      secondary={model.type === 'translate' ? 'Translation' : 'Transcription'}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        onChange={() => handleToggle(model)}
                        checked={isSelected}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
            })}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Google Models
              </Typography>
              <FormControlLabel
                sx={{ ml: 2 }}
                control={
                  <Checkbox
                    checked={selectAllState.google}
                    onChange={(e) => handleSelectAll('google', e.target.checked)}
                    indeterminate={
                      selectedModels.some(m => m.provider === 'google' && m.isEnabled) && 
                      !selectAllState.google
                    }
                    size="small"
                  />
                }
                label="Select All"
              />
            </Box>
            {availableModels
              .filter(model => model.provider === 'google')
              .map((model) => {
                const isSelected = selectedModels.some(m => m.id === model.id && m.isEnabled);
                return (
                  <ListItem key={model.id}>
                    <ListItemText 
                      primary={model.name}
                      secondary={model.type === 'translate' ? 'Translation' : 'Transcription'}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        onChange={() => handleToggle(model)}
                        checked={isSelected}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
            })}
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Other Services
              </Typography>
              <FormControlLabel
                sx={{ ml: 2 }}
                control={
                  <Checkbox
                    checked={selectAllState.other}
                    onChange={(e) => handleSelectAll('other', e.target.checked)}
                    indeterminate={
                      selectedModels.some(m => m.provider !== 'openai' && m.provider !== 'google' && m.isEnabled) && 
                      !selectAllState.other
                    }
                    size="small"
                  />
                }
                label="Select All"
              />
            </Box>
            {availableModels
              .filter(model => model.provider !== 'openai' && model.provider !== 'google')
              .map((model) => {
                const isSelected = selectedModels.some(m => m.id === model.id && m.isEnabled);
                return (
                  <ListItem key={model.id}>
                    <ListItemText 
                      primary={model.name}
                      secondary={model.type === 'translate' ? 'Translation' : 'Transcription'}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        onChange={() => handleToggle(model)}
                        checked={isSelected}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {selectedModels.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No models selected. Click "Add Model" to get started.
        </Typography>
      )}
    </Box>
  );
};

export default ModelSelector; 